import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

export const SubscriptionSuccessPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signIn } = useAuth()
  const [isLoggingIn, setIsLoggingIn] = useState(true)

  // Auto-login user after successful payment
  useEffect(() => {
    const email = searchParams.get('email')
    const password = searchParams.get('password')

    if (email && password) {
      // Auto-login the user
      signIn(decodeURIComponent(email), decodeURIComponent(password))
        .then(({ error }) => {
          if (error) {
            console.error('Auto-login failed:', error)
            // Redirect to login if auto-login fails
            navigate('/login')
          } else {
            setIsLoggingIn(false)
          }
        })
    } else {
      // No credentials in URL - user must already be logged in or came from different flow
      setIsLoggingIn(false)
    }
  }, [searchParams, signIn, navigate])

  if (isLoggingIn) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Completing your setup...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Logo */}
        <div className="mb-6 sm:mb-8">
          <img src="/black_logo.svg" alt="Helpdesk 411" className="h-8 sm:h-10 md:h-12 w-auto" />
        </div>

        {/* Progress Indicator - Step 3 active */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="flex space-x-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`h-2.5 sm:h-3 rounded-full ${
                  step === 3 ? 'bg-red-500 w-[50px]' : 'bg-gray-300 w-5'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Success Content */}
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
          {/* Success Circle */}
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-green-500 flex items-center justify-center mb-8">
            <Check className="w-16 h-16 sm:w-20 sm:h-20 text-green-500" strokeWidth={2} />
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">
            Transaction Successful
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-center max-w-md mb-8">
            You can now invite new users, this should just take a few seconds.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
            >
              Go to the Dashboard
            </button>
            <button
              onClick={() => navigate('/team')}
              className="px-8 py-3 bg-white text-gray-900 border border-gray-300 rounded-full font-medium hover:bg-gray-50 transition-colors"
            >
              Invite Team Members
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
